/**
 * Analytics aggregates — persistent, recording-gated counters that fill the
 * admin Analytics tab's real widgets (page detail views, auth funnel, login
 * failures). Separate from {@link ./trafficTracker} (which records ephemeral
 * cache/backend sessions): this module keeps rolling totals keyed by a stable
 * browser-instance id.
 *
 * Two independent recording toggles, controlled from the Analytics tab:
 *  - page-views recording → detail-page visits increment per-page hit counts
 *    (guest vs member) and track the set of unique browser instances seen.
 *  - auth recording → auth-funnel steps and login-failure categories.
 *
 * Per-browser localStorage (like the traffic tracker): the admin sees what
 * this browser captured. "Unique browser instances" only exceeds 1 with a
 * backend or when the same storage is shared — the aggregation is correct and
 * lights up once a server-persisted layer lands. Keys use the `analytics_`
 * prefix so the admin "Refresh Data" (`pc_*` wipe) leaves them intact.
 */

import { readJSON, writeJSON } from "@/utils/safeStorage";
import { enqueueGlobalAnalyticsEvent } from "@/utils/analyticsBeacon";

export type AnalyticsPageType =
    | "organization"
    | "event"
    | "regulation"
    | "article";

/**
 * Who viewed a detail page, resolved purely from local data (no extra backend
 * queries): the logged-in user from auth context vs. the page's own payload.
 *  - "owner": logged-in and their email matches the page's owner value (or
 *    its created-by value when owner is null/empty).
 *  - "admin": logged-in with role "admin" (and not the owner).
 *  - "user":  any other logged-in user.
 *  - "guest": not logged in.
 */
export type ViewerRole = "guest" | "user" | "admin" | "owner";

/**
 * Resolve the viewer's role for a detail page. Owner wins over admin (more
 * specific), then admin, then plain user. Pure comparison against the page's
 * already-fetched local data — never triggers a request.
 *
 * Owner is established by any of, in order of preference:
 *  - `ownedByCurrentUser` — pre-computed by the page when ownership lives
 *    outside the payload (organizations use `getOrgAccess(user, …)` against
 *    `user.memberships`; the org contact email is a public inbox, never
 *    matched).
 *  - the user's id matching the page's `ownerUserId` (identity-grade).
 *  - the user's email matching the page's owner email — or its created-by
 *    value when the owner email is null/empty.
 */
export function resolveViewerRole(args: {
    /** Logged-in user from auth context, or null/undefined when a guest. */
    user:
        | {
              id?: string | number | null;
              email?: string | null;
              role?: string | null;
          }
        | null
        | undefined;
    /** The page's owner value (an email) from its local payload, if any. */
    ownerEmail?: string | null;
    /** Fallback identity when owner is null — the page's created-by value. */
    createdBy?: string | null;
    /** The page's author/owner user id (e.g. envelope authorUserId). */
    ownerUserId?: string | number | null;
    /** Short-circuit for pages that derive ownership themselves. */
    ownedByCurrentUser?: boolean;
}): ViewerRole {
    const { user } = args;
    if (!user) return "guest";
    const norm = (v?: string | number | null) =>
        String(v ?? "")
            .trim()
            .toLowerCase();
    const userEmail = norm(user.email);
    const userId = norm(user.id);
    const pageOwnerEmail = norm(args.ownerEmail) || norm(args.createdBy);
    const pageOwnerId = norm(args.ownerUserId);
    const isOwner =
        args.ownedByCurrentUser === true ||
        (!!userId && !!pageOwnerId && userId === pageOwnerId) ||
        (!!userEmail && !!pageOwnerEmail && userEmail === pageOwnerEmail);
    if (isOwner) return "owner";
    if (norm(user.role) === "admin") return "admin";
    return "user";
}

export interface PageViewRow {
    key: string;
    type: AnalyticsPageType;
    title: string;
    slug: string;
    promoted: boolean;
    guestHits: number;
    /** Total logged-in hits (user + admin + owner). */
    memberHits: number;
    /** Logged-in, neither admin nor the page owner. */
    userHits: number;
    adminHits: number;
    ownerHits: number;
    uniqueBrowsers: number;
    lastTs: number;
}

export interface AuthAggregate {
    funnel: Record<string, number>;
    failures: Record<string, number>;
}

export interface AnalyticsAggregatesSnapshot {
    browserId: string;
    pageViewsRecording: boolean;
    authRecording: boolean;
    pageViews: PageViewRow[];
    auth: AuthAggregate;
}

// Canonical ordering shared with the admin tab.
export const FUNNEL_STEPS = [
    "splash_viewed",
    "cta_clicked",
    "gate_passed",
    "login_viewed",
    "magic_link_requested",
    "link_verified",
    "login_success",
] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

export const FAILURE_CATEGORIES = [
    "invalid_credentials",
    "magic_link_expired",
    "magic_link_already_used",
    "rate_limited",
    "email_delivery_failed",
    "network_error",
] as const;
export type FailureCategory = (typeof FAILURE_CATEGORIES)[number];

const K_BROWSER = "analytics_tracker.browserId";
const K_PV_REC = "analytics_agg.pvRecording";
const K_AUTH_REC = "analytics_agg.authRecording";
const K_PV_DATA = "analytics_agg.pageViews";
const K_AUTH_DATA = "analytics_agg.auth";

const isBrowser = typeof window !== "undefined";

interface PageViewStored {
    key: string;
    type: AnalyticsPageType;
    title: string;
    slug: string;
    promoted: boolean;
    guestHits: number;
    /** Total logged-in hits (kept as the user+admin+owner sum). */
    memberHits: number;
    /** Role breakdown — rows stored before the split lack these (see load). */
    userHits?: number;
    adminHits?: number;
    ownerHits?: number;
    browsers: string[];
    lastTs: number;
}

function makeBrowserId(): string {
    try {
        if (
            isBrowser &&
            typeof window.crypto?.randomUUID === "function"
        ) {
            return window.crypto.randomUUID();
        }
    } catch {
        // fall through
    }
    return `b_${Date.now().toString(36)}_${Math.floor(
        Math.random() * 1e9,
    ).toString(36)}`;
}

function emptyAuth(): AuthAggregate {
    return { funnel: {}, failures: {} };
}

class AnalyticsAggregates {
    private browserId = "";
    private pvRecording = false;
    private authRecording = false;
    private pageViews = new Map<string, PageViewStored>();
    private auth: AuthAggregate = emptyAuth();
    private listeners = new Set<() => void>();
    private snapshot: AnalyticsAggregatesSnapshot;

    constructor() {
        if (isBrowser) {
            this.browserId = readJSON<string>(K_BROWSER, "");
            if (!this.browserId) {
                this.browserId = makeBrowserId();
                writeJSON(K_BROWSER, this.browserId);
            }
            this.pvRecording = readJSON<boolean>(K_PV_REC, false);
            this.authRecording = readJSON<boolean>(K_AUTH_REC, false);
            const storedPv = readJSON<PageViewStored[]>(K_PV_DATA, []);
            // Rows stored before the role split have only guest/member: fold
            // the legacy member total into userHits so sums stay consistent.
            storedPv.forEach((r) =>
                this.pageViews.set(r.key, {
                    ...r,
                    userHits: r.userHits ?? r.memberHits ?? 0,
                    adminHits: r.adminHits ?? 0,
                    ownerHits: r.ownerHits ?? 0,
                }),
            );
            this.auth = {
                ...emptyAuth(),
                ...readJSON<AuthAggregate>(K_AUTH_DATA, emptyAuth()),
            };
        }
        this.snapshot = this.build();
    }

    getSnapshot = (): AnalyticsAggregatesSnapshot => this.snapshot;

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    private build(): AnalyticsAggregatesSnapshot {
        const pageViews: PageViewRow[] = [...this.pageViews.values()].map(
            (r) => ({
                key: r.key,
                type: r.type,
                title: r.title,
                slug: r.slug,
                promoted: r.promoted,
                guestHits: r.guestHits,
                memberHits: r.memberHits,
                userHits: r.userHits ?? 0,
                adminHits: r.adminHits ?? 0,
                ownerHits: r.ownerHits ?? 0,
                uniqueBrowsers: r.browsers.length,
                lastTs: r.lastTs,
            }),
        );
        return {
            browserId: this.browserId,
            pageViewsRecording: this.pvRecording,
            authRecording: this.authRecording,
            pageViews,
            auth: {
                funnel: { ...this.auth.funnel },
                failures: { ...this.auth.failures },
            },
        };
    }

    private notify(): void {
        this.snapshot = this.build();
        this.listeners.forEach((l) => l());
    }

    // ── Controls ──
    setPageViewsRecording(on: boolean): void {
        this.pvRecording = on;
        writeJSON(K_PV_REC, on);
        this.notify();
    }

    setAuthRecording(on: boolean): void {
        this.authRecording = on;
        writeJSON(K_AUTH_REC, on);
        this.notify();
    }

    clearPageViews(): void {
        this.pageViews.clear();
        writeJSON(K_PV_DATA, []);
        this.notify();
    }

    clearAuth(): void {
        this.auth = emptyAuth();
        writeJSON(K_AUTH_DATA, this.auth);
        this.notify();
    }

    // ── Emit ──
    recordPageView(args: {
        type: AnalyticsPageType;
        title: string;
        slug: string;
        promoted?: boolean;
        viewerRole: ViewerRole;
    }): void {
        if (!this.pvRecording || !isBrowser) return;
        const key = `${args.type}:${args.slug || args.title}`;
        const existing = this.pageViews.get(key);
        const row: PageViewStored = existing ?? {
            key,
            type: args.type,
            title: args.title,
            slug: args.slug,
            promoted: !!args.promoted,
            guestHits: 0,
            memberHits: 0,
            userHits: 0,
            adminHits: 0,
            ownerHits: 0,
            browsers: [],
            lastTs: 0,
        };
        if (args.viewerRole === "guest") {
            row.guestHits += 1;
        } else {
            row.memberHits += 1;
            if (args.viewerRole === "owner") {
                row.ownerHits = (row.ownerHits ?? 0) + 1;
            } else if (args.viewerRole === "admin") {
                row.adminHits = (row.adminHits ?? 0) + 1;
            } else {
                row.userHits = (row.userHits ?? 0) + 1;
            }
        }
        if (!row.browsers.includes(this.browserId)) {
            row.browsers.push(this.browserId);
        }
        // Keep the freshest title/promoted flag.
        row.title = args.title || row.title;
        if (args.promoted !== undefined) row.promoted = args.promoted;
        row.lastTs = Date.now();
        this.pageViews.set(key, row);
        writeJSON(K_PV_DATA, [...this.pageViews.values()]);
        this.notify();
    }

    recordAuthStep(step: FunnelStep): void {
        if (!this.authRecording || !isBrowser) return;
        this.auth.funnel[step] = (this.auth.funnel[step] ?? 0) + 1;
        writeJSON(K_AUTH_DATA, this.auth);
        this.notify();
    }

    recordAuthFailure(category: FailureCategory): void {
        if (!this.authRecording || !isBrowser) return;
        this.auth.failures[category] =
            (this.auth.failures[category] ?? 0) + 1;
        writeJSON(K_AUTH_DATA, this.auth);
        this.notify();
    }
}

export const analyticsAggregates = new AnalyticsAggregates();

// ── Emit helpers used by instrumentation points ──
// Each helper feeds two sinks with independent gates: the local per-browser
// aggregates (admin "Recording …" toggles) and the global-traffic beacon
// (gated by the "Track global traffic" site setting, enforced server-side
// too — see utils/analyticsBeacon.ts).
export function trackPageView(args: {
    type: AnalyticsPageType;
    title: string;
    slug: string;
    promoted?: boolean;
    viewerRole: ViewerRole;
}): void {
    analyticsAggregates.recordPageView(args);
    enqueueGlobalAnalyticsEvent({
        kind: "page_view",
        role: args.viewerRole,
        pageType: args.type,
        slug: args.slug,
        title: args.title,
        promoted: args.promoted,
    });
}

export function trackAuthStep(step: FunnelStep): void {
    analyticsAggregates.recordAuthStep(step);
    enqueueGlobalAnalyticsEvent({ kind: "auth_step", name: step });
}

export function trackAuthFailure(category: FailureCategory): void {
    analyticsAggregates.recordAuthFailure(category);
    enqueueGlobalAnalyticsEvent({ kind: "auth_failure", name: category });
}

/**
 * Map a magic-link / login error message to a failure category. The backend
 * deliberately keeps these messages coarse (anti-enumeration), so we match on
 * the categorizable ones and default to invalid_credentials.
 */
export function categorizeAuthError(message: string): FailureCategory {
    const m = (message || "").toLowerCase();
    // Connectivity failures (fetch throws) — bucket separately so a genuine
    // outage doesn't inflate the credential/brute-force signal.
    if (/failed to fetch|load failed|networkerror|network request failed/.test(m))
        return "network_error";
    if (/already used|already been used/.test(m))
        return "magic_link_already_used";
    if (/too many|rate[-\s]?limit|throttl/.test(m)) return "rate_limited";
    if (/unable to send|deliver/.test(m)) return "email_delivery_failed";
    // The backend's generic "Invalid or expired token" means the token was
    // not found — treat as invalid, not expired. Only a bare "expired" (e.g.
    // "Token expired") counts as an expiry.
    if (/invalid or expired/.test(m)) return "invalid_credentials";
    if (/expired/.test(m)) return "magic_link_expired";
    return "invalid_credentials";
}
